/**
 * Platform Preview Renderers
 * Hyper-realistic social media post previews with inline SVG icons.
 *
 * Styling model (TOKEN-MIG-B): platform chrome (brand colors, platform-native
 * grays, white cards, letterboxes) lives in the `.pv-*` classes at the end of
 * app/styles.css — those colors are deliberately theme-fixed. Inline styles
 * here are layout-only, except genuinely dynamic bits (object-fit, grid
 * spans, carousel state).
 */
import { escapeHtml } from "./escapeHtml.js";
import { clampMediaForPlatform } from "../data/mediaLimits.js";

// ---------------------------------------------------------------------------
// SVG icon library (inline strings, no emojis)
// ---------------------------------------------------------------------------

const ICONS = {
  globe12: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',

  // Facebook
  fbLike: '<svg width="18" height="18" viewBox="0 0 24 24"><path d="M2 21h4V9H2v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" fill="currentColor"/></svg>',
  fbComment: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  fbShare: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>',

  // Instagram
  igHeart: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
  igComment: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
  igSend: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>',
  igBookmark: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>',

  // X / Twitter
  xReply: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
  xRetweet: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>',
  xHeart: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
  xChart: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>',
  xShare: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>',
  xVerified: '<svg width="14" height="14" viewBox="0 0 22 22"><path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.69-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.636.433 1.221.878 1.69.47.446 1.055.752 1.69.883.635.13 1.294.083 1.902-.14.272.587.702 1.088 1.24 1.44.539.354 1.167.551 1.813.568.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.222 1.26.268 1.893.14.634-.133 1.218-.438 1.689-.884.445-.47.75-1.055.88-1.69.13-.634.075-1.293-.145-1.9.587-.274 1.084-.705 1.438-1.246.355-.54.553-1.17.57-1.816z" fill="#1D9BF0"/><path d="M9.585 14.929l-3.292-3.293a.999.999 0 0 1 1.414-1.414l2.171 2.172 5.02-5.593a.998.998 0 0 1 1.494 1.326l-5.72 6.374a1 1 0 0 1-.744.346.998.998 0 0 1-.343-.118z" fill="#fff"/></svg>',

  // LinkedIn
  liLike: '<svg width="16" height="16" viewBox="0 0 24 24"><path d="M2 21h4V9H2v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" fill="currentColor"/></svg>',
  liComment: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  liRepost: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>',
  liSend: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>',

  // TikTok
  ttHeart: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
  ttComment: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  ttShare: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>',
  ttBookmark: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>',

  // YouTube
  ytLike: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>',
  ytDislike: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>',
  ytShare: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name) {
  if (!name) return "??";
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatText(text) {
  if (!text) return "";
  const escaped = escapeHtml(text);
  return escaped
    // Fix double-encoded entities from AI output (e.g. &amp;#039; → &#039; which renders as ')
    .replace(/&amp;#039;/g, "&#039;")
    .replace(/&amp;amp;/g, "&amp;")
    .replace(/&amp;quot;/g, "&quot;")
    .replace(/\n|&#10;/g, "<br>")
    // Hashtags: match # only when NOT preceded by & (so we don't match inside entities like &#039;)
    .replace(/(^|[^&\w])#([A-Za-z][\w]*)/g, '$1<span class="pv-hashtag">#$2</span>');
}

// Platform-accurate body folding. Cuts the RAW text (before escaping) at a
// word boundary near `limit`, so formatText still sees intact hashtag tokens.
function foldText(text, limit) {
  const t = text || "";
  if (t.length <= limit) return { text: t, folded: false };
  let cut = t.slice(0, limit);
  const brk = Math.max(cut.lastIndexOf(" "), cut.lastIndexOf("\n"));
  if (brk > limit * 0.6) cut = cut.slice(0, brk);
  return { text: cut, folded: true };
}

// ---------------------------------------------------------------------------
// Facebook Preview
// ---------------------------------------------------------------------------

export function renderFacebookPreview(text, brandName = "Heist Brand", media) {
  const safeBrand = escapeHtml(brandName);
  const initials = getInitials(brandName);

  return `
    <div data-testid="preview-facebook" class="pv-fb-card">
      <!-- Header -->
      <div style="display:flex;align-items:center;padding:12px 16px 0">
        <div class="pv-avatar-ph pv-avatar-ph--md">${escapeHtml(initials)}</div>
        <div style="margin-left:8px;flex:1;min-width:0">
          <div class="pv-fb-name">${safeBrand}</div>
          <div class="pv-fb-muted" style="font-size:12px;display:flex;align-items:center;gap:4px">Just now <span style="display:inline-flex">${ICONS.globe12}</span></div>
        </div>
        <div class="pv-fb-muted" style="font-size:20px;cursor:pointer;padding:0 4px">...</div>
      </div>

      <!-- Body -->
      <div class="pv-body">${formatText(text)}</div>

      <!-- Image / video grid -->
      ${media && media.length > 0 ? renderFacebookGrid(clampMediaForPlatform('facebook', media)) : '<div class="pv-media-ph" style="width:100%;height:260px"></div>'}

      <!-- Actions bar (zero-state: a new post has no reactions yet) -->
      <div class="pv-fb-actionbar">
        <button class="pv-fb-action"><span style="display:inline-flex">${ICONS.fbLike}</span> Like</button>
        <button class="pv-fb-action"><span style="display:inline-flex">${ICONS.fbComment}</span> Comment</button>
        <button class="pv-fb-action"><span style="display:inline-flex">${ICONS.fbShare}</span> Share</button>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Instagram Preview
// ---------------------------------------------------------------------------

export function renderInstagramPreview(text, brandName = "heist_brand", media) {
  const handle = brandName.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
  const safeHandle = escapeHtml(handle);
  const initials = getInitials(brandName);

  return `
    <div data-testid="preview-instagram" class="pv-ig-card">
      <!-- Header -->
      <div style="display:flex;align-items:center;padding:10px 12px">
        <!-- Story ring avatar -->
        <div class="pv-ig-ring">
          <div class="pv-avatar-ph">${escapeHtml(initials)}</div>
        </div>
        <div style="margin-left:10px;flex:1;min-width:0">
          <div class="pv-ig-ink" style="font-size:14px;font-weight:700">${safeHandle}</div>
        </div>
        <div class="pv-ig-ink" style="font-size:16px;font-weight:700;cursor:pointer;letter-spacing:2px">...</div>
      </div>

      <!-- Carousel (1:1) -->
      ${media && media.length > 0 ? renderInstagramCarousel(clampMediaForPlatform('instagram', media)) : '<div class="pv-media-ph" style="width:100%;aspect-ratio:1;max-height:468px"></div>'}

      <!-- Actions -->
      <div style="display:flex;align-items:center;padding:8px 16px 0;gap:14px">
        <span class="pv-ig-ink" style="display:inline-flex;cursor:pointer">${ICONS.igHeart}</span>
        <span class="pv-ig-ink" style="display:inline-flex;cursor:pointer">${ICONS.igComment}</span>
        <span class="pv-ig-ink" style="display:inline-flex;cursor:pointer">${ICONS.igSend}</span>
        <span class="pv-ig-ink" style="display:inline-flex;cursor:pointer;margin-left:auto">${ICONS.igBookmark}</span>
      </div>

      <!-- Caption (zero-state: no fabricated like/comment counts) -->
      <div class="pv-body">
        <span style="font-weight:700">${safeHandle}</span> ${formatText(text)}
      </div>

      <!-- Timestamp -->
      <div class="pv-ig-muted" style="padding:0 16px 12px;font-size:11px;text-transform:uppercase">Just now</div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// X / Twitter Preview
// ---------------------------------------------------------------------------

export function renderXPreview(text, brandName = "Heist", media) {
  const safeBrand = escapeHtml(brandName);
  let handle = brandName.replace(/[^a-zA-Z0-9_]/g, "");
  if (!handle) {
    handle = brandName.toLowerCase().replace(/[^a-z0-9_]/g, "").trim() || "yourbrand";
  }
  const safeHandle = escapeHtml(handle);
  const initials = getInitials(brandName);
  // X hard-caps posts at 280 characters — the preview shows what would post.
  const fold = foldText(text, 280);
  const bodyHtml = formatText(fold.text) + (fold.folded ? '…' : '');

  return `
    <div data-testid="preview-x" class="pv-x-card">
      <div style="display:flex;gap:12px">
        <!-- Avatar -->
        <div class="pv-avatar-ph pv-avatar-ph--lg">${escapeHtml(initials)}</div>

        <!-- Content -->
        <div style="flex:1;min-width:0">
          <!-- Header -->
          <div style="display:flex;align-items:center;gap:4px;flex-wrap:wrap">
            <span class="pv-x-name">${safeBrand}</span>
            <span style="display:inline-flex">${ICONS.xVerified}</span>
            <span class="pv-x-muted">@${safeHandle}</span>
            <span class="pv-x-muted">·</span>
            <span class="pv-x-muted">now</span>
          </div>

          <!-- Body -->
          <div class="pv-body">${bodyHtml}</div>

          <!-- Media -->
          ${media && media.length > 0 ? `<div class="pv-x-mediaframe">${renderXGrid(clampMediaForPlatform('x', media))}</div>` : ''}

          <!-- Actions (zero-state: a new post has no engagement yet) -->
          <div style="display:flex;justify-content:space-between;max-width:425px;margin-top:8px">
            <div class="pv-x-action pv-x-action--blue"><span style="display:inline-flex">${ICONS.xReply}</span></div>
            <div class="pv-x-action pv-x-action--green"><span style="display:inline-flex">${ICONS.xRetweet}</span></div>
            <div class="pv-x-action pv-x-action--pink"><span style="display:inline-flex">${ICONS.xHeart}</span></div>
            <div class="pv-x-action pv-x-action--blue"><span style="display:inline-flex">${ICONS.xChart}</span></div>
            <div class="pv-x-action pv-x-action--blue"><span style="display:inline-flex">${ICONS.xShare}</span></div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// LinkedIn Preview
// ---------------------------------------------------------------------------

export function renderLinkedInPreview(text, brandName = "Heist Enterprise", media) {
  const safeBrand = escapeHtml(brandName);
  const initials = getInitials(brandName);
  // LinkedIn folds the feed view of a post at ~210 characters behind "…see more".
  const fold = foldText(text, 210);
  const bodyHtml = formatText(fold.text) +
    (fold.folded ? '<span class="pv-li-seemore"> &hellip;see more</span>' : '');

  return `
    <div data-testid="preview-linkedin" class="pv-li-card">
      <!-- Header -->
      <div style="display:flex;align-items:flex-start;padding:12px 16px 0">
        <div class="pv-avatar-ph pv-avatar-ph--lg">${escapeHtml(initials)}</div>
        <div style="margin-left:8px;flex:1;min-width:0">
          <div class="pv-li-ink" style="font-size:14px;font-weight:700">${safeBrand}</div>
          <div class="pv-li-muted" style="font-size:12px">Enterprise Content Operations Platform</div>
          <div class="pv-li-muted" style="font-size:12px;display:flex;align-items:center;gap:4px">Just now <span style="display:inline-flex">${ICONS.globe12}</span></div>
        </div>
        <div class="pv-li-muted" style="font-size:20px;cursor:pointer;padding:0 4px">...</div>
      </div>

      <!-- Body -->
      <div class="pv-body">${bodyHtml}</div>

      <!-- Image / video grid -->
      ${media && media.length > 0 ? renderLinkedInGrid(clampMediaForPlatform('linkedin', media)) : '<div class="pv-media-ph" style="width:100%;height:280px"></div>'}

      <!-- Actions bar (zero-state: a new post has no reactions yet) -->
      <div class="pv-li-actionbar">
        <button class="pv-li-action"><span style="display:inline-flex">${ICONS.liLike}</span> Like</button>
        <button class="pv-li-action"><span style="display:inline-flex">${ICONS.liComment}</span> Comment</button>
        <button class="pv-li-action"><span style="display:inline-flex">${ICONS.liRepost}</span> Repost</button>
        <button class="pv-li-action"><span style="display:inline-flex">${ICONS.liSend}</span> Send</button>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Blog Preview
// ---------------------------------------------------------------------------

export function renderBlogPreview(text, title = "Blog Post") {
  const safeTitle = escapeHtml(title || "Untitled Blog Post");

  return `
    <div data-testid="preview-blog" class="pv-blog-card">
      <!-- Hero image placeholder -->
      <div class="pv-media-ph" style="width:100%;height:200px"></div>

      <div style="padding:32px 40px">
        <h1 class="pv-title">${safeTitle}</h1>
        <div class="pv-meta-line">Published just now</div>
        <div class="pv-body">${formatText(text)}</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Website Preview (Heist-hosted blog)
// ---------------------------------------------------------------------------

export function renderWebsitePreview(text, title = "Blog Post", brandName = "Your Site") {
  const safeTitle = escapeHtml(title || "Untitled Blog Post");
  const safeBrand = escapeHtml(brandName || "Your Site");

  return `
    <div data-testid="preview-website" class="pv-web-card">
      <!-- Site header -->
      <div class="pv-web-header">
        <span class="pv-web-brand">${safeBrand}</span>
        <span class="pv-web-navtag">Blog</span>
      </div>

      <div style="padding:32px 24px">
        <span class="pv-web-chip">Article</span>
        <h1 class="pv-web-h1">${safeTitle}</h1>
        <div class="pv-web-meta">Published just now</div>
        <div class="pv-body">${formatText(text)}</div>
      </div>

      <!-- CTA footer -->
      <div class="pv-web-cta">
        <div class="pv-web-cta-note">Powered by Heist</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Per-platform media grids
// ---------------------------------------------------------------------------

// Shape-tolerant type predicates. Two flavors of media object exist:
//   - File-upload  — type:"image/jpeg" | "video/mp4"  (full MIME)
//   - Server-side  — type:"image" | "video"           (short)
// The platform grid filters used to test only the MIME prefix
// (`type.startsWith('image/')`), which silently filtered out every
// server-attached row → empty grid → black bg → broken-thumbnail bug.
function mediaIsImage(m) {
  if (!m) return false;
  const t = String(m.type || '').toLowerCase();
  if (t.startsWith('image/') || t === 'image') return true;
  // Heuristic fallback: no type, but URL extension is image-y.
  if (!t) {
    const src = m.dataUrl || m.url || m.thumbUrl || '';
    return /\.(jpe?g|png|gif|webp|avif|heic)(\?|$)/i.test(src);
  }
  return false;
}
function mediaIsVideoFn(m) {
  if (!m) return false;
  const t = String(m.type || '').toLowerCase();
  if (t.startsWith('video/') || t === 'video') return true;
  if (!t) {
    const src = m.dataUrl || m.url || m.thumbUrl || '';
    return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(src);
  }
  return false;
}

// Render a single media item as either a <video controls> or <img>. Used by
// the grid renderers as a building block. `fit` controls object-fit (cover
// for tiled grids, contain for single-item display).
function renderSingleMediaItem(m, fit = 'cover') {
  if (!m) return '';
  // Shape-tolerant: server-attached items have `url` (no dataUrl) and a
  // short type like "image"; user-uploaded items have `dataUrl` and a
  // MIME type like "image/jpeg". Treat both as first-class.
  const src = m.dataUrl || m.url || m.thumbUrl || '';
  const t = String(m.type || '').toLowerCase();
  const extLooksVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(src);
  const isVideo = t.startsWith('video/') || t === 'video' || (!t && extLooksVideo);
  if (isVideo) {
    return `<video src="${src}" class="pv-letterbox" style="width:100%;height:100%;object-fit:${fit};display:block" controls playsinline preload="metadata"></video>`;
  }
  const alt = m.altText || m.name || m.originalName || 'Attached media';
  return `<img src="${src}" style="width:100%;height:100%;object-fit:${fit};display:block" alt="${alt}">`;
}

// Render the "+N more" overlay on the last tile of a grid.
function overlayMore(n) {
  return `<div class="pv-more-overlay">+${n}</div>`;
}

// Wrap a tile with positioning.
function tile(content, extraStyle = '') {
  return `<div class="pv-tile"${extraStyle ? ` style="${extraStyle}"` : ''}>${content}</div>`;
}

/**
 * Facebook-style media grid.
 * - 1: single full-width item (16:9)
 * - 2: side-by-side, each 1:1
 * - 3: 1 big left + 2 stacked right
 * - 4: 2x2 grid, all 1:1
 * - 5+: same as 4 but "+N" overlay on the 4th tile
 * Works for images, videos, or mixed.
 */
function renderFacebookGrid(media) {
  const n = media.length;
  if (n === 0) return '';
  if (n === 1) {
    return `<div class="pv-letterbox" style="width:100%;aspect-ratio:16/9">${renderSingleMediaItem(media[0], 'cover')}</div>`;
  }
  if (n === 2) {
    return `<div class="pv-letterbox" style="display:grid;grid-template-columns:1fr 1fr;gap:2px;width:100%;aspect-ratio:2/1">
      ${tile(renderSingleMediaItem(media[0]))}
      ${tile(renderSingleMediaItem(media[1]))}
    </div>`;
  }
  if (n === 3) {
    return `<div class="pv-letterbox" style="display:grid;grid-template-columns:2fr 1fr;grid-template-rows:1fr 1fr;gap:2px;width:100%;aspect-ratio:4/3">
      ${tile(renderSingleMediaItem(media[0]), 'grid-row:1/3')}
      ${tile(renderSingleMediaItem(media[1]))}
      ${tile(renderSingleMediaItem(media[2]))}
    </div>`;
  }
  // 4 or more
  const extra = n - 4;
  return `<div class="pv-letterbox" style="display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:2px;width:100%;aspect-ratio:1/1">
    ${media.slice(0, 4).map((m, i) => tile(
      renderSingleMediaItem(m) + (i === 3 && extra > 0 ? overlayMore(extra) : '')
    )).join('')}
  </div>`;
}

/**
 * Instagram carousel — single square frame with dot indicators and a swipe
 * arrow hint when > 1 item. Scrollable horizontally.
 */
function renderInstagramCarousel(media) {
  const n = media.length;
  if (n === 0) return '';
  if (n === 1) {
    return `<div class="pv-letterbox" style="width:100%;aspect-ratio:1/1;max-height:468px;overflow:hidden">${renderSingleMediaItem(media[0], 'cover')}</div>`;
  }
  // Multi: horizontal scroll with snap, counter badge top-right, dot row below.
  const items = media.map((m) => `
    <div class="pv-letterbox" style="flex:0 0 100%;aspect-ratio:1/1;max-height:468px;scroll-snap-align:start;position:relative">
      ${renderSingleMediaItem(m, 'cover')}
    </div>
  `).join('');
  return `
    <div style="position:relative;width:100%">
      <div style="display:flex;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;-ms-overflow-style:none" onscroll="(function(el){ const w=el.clientWidth; const idx=Math.round(el.scrollLeft/w); const dots=el.parentElement.querySelectorAll('.pv-ig-dot'); dots.forEach((d,i)=>d.classList.toggle('pv-ig-dot--active',i===idx)); const c=el.parentElement.querySelector('.pv-ig-count'); if(c) c.textContent=(idx+1)+'/'+dots.length; })(this)">${items}</div>
      <div class="pv-count-badge pv-ig-count">1/${n}</div>
      <div style="display:flex;justify-content:center;gap:6px;padding:10px 0 2px">
        ${media.map((_, i) => `<span class="pv-ig-dot${i === 0 ? ' pv-ig-dot--active' : ''}"></span>`).join('')}
      </div>
    </div>
  `;
}

/**
 * X (Twitter) media grid. Strictly images-only unless single video.
 * - 1: full-width 16:9
 * - 2: side-by-side, 2:1 aspect
 * - 3: 1 left + 2 stacked right
 * - 4: 2x2
 */
function renderXGrid(media) {
  const n = media.length;
  if (n === 0) return '';
  // X only allows 1 video OR up to 4 images. If there's a video, treat as single.
  const hasVideo = media.some((m) => mediaIsVideoFn(m));
  if (hasVideo) {
    const video = media.find((m) => mediaIsVideoFn(m));
    return `<div class="pv-letterbox" style="width:100%;aspect-ratio:16/9;border-radius:inherit;overflow:hidden">${renderSingleMediaItem(video, 'cover')}</div>`;
  }
  // Images only, max 4
  const imgs = media.filter((m) => mediaIsImage(m)).slice(0, 4);
  if (imgs.length === 1) {
    return `<div class="pv-letterbox" style="width:100%;aspect-ratio:16/9">${renderSingleMediaItem(imgs[0])}</div>`;
  }
  if (imgs.length === 2) {
    return `<div class="pv-letterbox" style="display:grid;grid-template-columns:1fr 1fr;gap:2px;width:100%;aspect-ratio:2/1">
      ${tile(renderSingleMediaItem(imgs[0]))}
      ${tile(renderSingleMediaItem(imgs[1]))}
    </div>`;
  }
  if (imgs.length === 3) {
    return `<div class="pv-letterbox" style="display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:2px;width:100%;aspect-ratio:16/9">
      ${tile(renderSingleMediaItem(imgs[0]), 'grid-row:1/3')}
      ${tile(renderSingleMediaItem(imgs[1]))}
      ${tile(renderSingleMediaItem(imgs[2]))}
    </div>`;
  }
  return `<div class="pv-letterbox" style="display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:2px;width:100%;aspect-ratio:16/9">
    ${imgs.map((m) => tile(renderSingleMediaItem(m))).join('')}
  </div>`;
}

/**
 * LinkedIn media grid — very similar to Facebook but tops out the visible
 * grid at 4 tiles with "+N more" overlay.
 */
function renderLinkedInGrid(media) {
  // LinkedIn doesn't allow mixing; if a video is present, just show the video.
  const hasVideo = media.some((m) => mediaIsVideoFn(m));
  if (hasVideo) {
    const v = media.find((m) => mediaIsVideoFn(m));
    return `<div class="pv-letterbox" style="width:100%;aspect-ratio:16/9">${renderSingleMediaItem(v, 'cover')}</div>`;
  }
  return renderFacebookGrid(media);
}

/**
 * TikTok — single full-screen video/image with a "+N" badge if the carousel
 * has more items (photo carousels supported).
 */
function renderTikTokMedia(media) {
  const n = media.length;
  if (n === 0) return '';
  const first = media[0];
  const badge = n > 1
    ? `<div class="pv-count-badge" style="font-size:11px;z-index:3;display:flex;align-items:center;gap:4px"><svg width="10" height="10" viewBox="0 0 24 24" fill="#fff"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>1/${n}</div>`
    : '';
  return `<div style="position:relative;width:100%;height:100%">
    ${renderSingleMediaItem(first, 'cover')}
    ${badge}
  </div>`;
}

// Dispatcher kept for backwards compatibility (older callers pass style
// 'default' or 'instagram'). New code should call the platform-specific
// renderers directly.
function renderMediaBlock(media, style = 'default') {
  if (!media || media.length === 0) return '';
  if (style === 'instagram') return renderInstagramCarousel(media);
  return renderFacebookGrid(media);
}

// ---------------------------------------------------------------------------
// TikTok Preview
// ---------------------------------------------------------------------------
export function renderTikTokPreview(text, brandName = "heist_brand", media) {
    const handle = brandName.replace(/[^a-zA-Z0-9_.]/g, '_').toLowerCase();
    const safeHandle = escapeHtml(handle);
    const initials = getInitials(brandName);

    return `
        <div data-testid="preview-tiktok" class="pv-tt-card">
            <div class="pv-tt-scrim"></div>
            ${media && media.length > 0
                ? `<div style="position:absolute;inset:0;z-index:2">${renderTikTokMedia(clampMediaForPlatform('tiktok', media))}</div>`
                : '<div class="pv-tt-ph"></div>'
            }
            <div class="pv-tt-rail">
                <div class="pv-avatar-ph pv-avatar-ph--md pv-avatar-ph--ring">${escapeHtml(initials)}</div>
                <div class="pv-tt-rail-item"><span style="display:block">${ICONS.ttHeart}</span><span class="pv-tt-rail-label">0</span></div>
                <div class="pv-tt-rail-item"><span style="display:block">${ICONS.ttComment}</span><span class="pv-tt-rail-label">0</span></div>
                <div class="pv-tt-rail-item"><span style="display:block">${ICONS.ttBookmark}</span><span class="pv-tt-rail-label">0</span></div>
                <div class="pv-tt-rail-item"><span style="display:block">${ICONS.ttShare}</span><span class="pv-tt-rail-label">Share</span></div>
            </div>
            <div class="pv-tt-caption">
                <div style="font-size:14px;font-weight:700;margin-bottom:4px">@${safeHandle}</div>
                <div class="pv-body">${formatText(text)}</div>
                <div style="margin-top:8px;display:flex;align-items:center;gap:6px">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><circle cx="12" cy="12" r="10"/><path d="M9 8l7 4-7 4V8z" fill="#000"/></svg>
                    <span style="font-size:11px">Original Sound - ${safeHandle}</span>
                </div>
            </div>
        </div>
    `;
}

// ---------------------------------------------------------------------------
// YouTube Preview
// ---------------------------------------------------------------------------
export function renderYouTubePreview(text, title, brandName = "Heist", media) {
    const safeBrand = escapeHtml(brandName);
    const safeTitle = escapeHtml(title || 'Untitled Video');
    const initials = getInitials(brandName);

    return `
        <div data-testid="preview-youtube" class="pv-yt-card">
            <div class="pv-letterbox" style="width:100%;aspect-ratio:16/9;position:relative;overflow:hidden">
                ${media && media.length > 0
                    ? renderSingleMediaItem(clampMediaForPlatform('youtube', media)[0], 'cover')
                    : '<div class="pv-media-ph" style="width:100%;height:100%"></div>'
                }
                ${!(media && media.length > 0 && mediaIsVideoFn(media[0])) ? `
                <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none">
                    <div class="pv-yt-play">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                </div>
                ` : ''}
                <div class="pv-yt-badge">10:24</div>
            </div>
            <div style="display:flex;gap:12px;padding:12px 16px">
                <div class="pv-avatar-ph pv-avatar-ph--sm">${escapeHtml(initials)}</div>
                <div style="flex:1;min-width:0">
                    <div class="pv-yt-title">${safeTitle}</div>
                    <div class="pv-yt-muted" style="font-size:12px;margin-top:4px">${safeBrand} &middot; No views &middot; Just now</div>
                </div>
            </div>
            ${text ? `<div style="padding:0 16px 12px;margin-left:48px"><div class="pv-body">${formatText(text)}</div></div>` : ''}
            <div style="display:flex;padding:8px 16px 12px;gap:16px;margin-left:48px">
                <div class="pv-yt-muted" style="display:flex;align-items:center;gap:4px;cursor:pointer"><span style="display:inline-flex">${ICONS.ytLike}</span><span style="font-size:12px">Like</span></div>
                <div class="pv-yt-muted" style="display:flex;align-items:center;gap:4px;cursor:pointer"><span style="display:inline-flex">${ICONS.ytDislike}</span></div>
                <div class="pv-yt-muted" style="display:flex;align-items:center;gap:4px;cursor:pointer"><span style="display:inline-flex">${ICONS.ytShare}</span><span style="font-size:12px">Share</span></div>
            </div>
        </div>
    `;
}

// ---------------------------------------------------------------------------
// Pinterest Preview — image-first Pin card with title + description.
// ---------------------------------------------------------------------------
export function renderPinterestPreview(text, title, brandName = "Heist", media) {
    const safeBrand = escapeHtml(brandName);
    const safeTitle = escapeHtml(title || '');
    const initials = getInitials(brandName);
    return `
        <div data-testid="preview-pinterest" class="pv-pin-card">
            <div class="pv-letterbox" style="width:100%;aspect-ratio:2/3;position:relative;overflow:hidden">
                ${media && media.length > 0
                    ? renderSingleMediaItem(clampMediaForPlatform('pinterest', media)[0], 'cover')
                    : '<div class="pv-pin-ph"></div>'
                }
                <div class="pv-pin-save">Save</div>
            </div>
            <div style="padding:10px 12px">
                ${safeTitle ? `<div class="pv-pin-title">${safeTitle}</div>` : ''}
                <div class="pv-body">${formatText(text)}</div>
                <div style="display:flex;align-items:center;gap:8px;margin-top:10px">
                    <div class="pv-avatar-ph pv-avatar-ph--xs">${escapeHtml(initials)}</div>
                    <span class="pv-pin-brand">${safeBrand}</span>
                </div>
            </div>
        </div>
    `;
}

// ---------------------------------------------------------------------------
// Reddit Preview — subreddit header + title + self-text body.
// ---------------------------------------------------------------------------
export function renderRedditPreview(text, title, brandName = "Heist", media) {
    const safeTitle = escapeHtml(title || 'An interesting discussion');
    const handle = escapeHtml(String(brandName || 'heist').replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase());
    return `
        <div data-testid="preview-reddit" class="pv-rd-card">
            <div style="display:flex;align-items:center;gap:8px;padding:10px 12px 6px">
                <div class="pv-rd-sub-avatar">r/</div>
                <div class="pv-rd-subline"><span style="font-weight:700">r/yoursubreddit</span> <span class="pv-rd-muted">&middot; Posted by u/${handle} &middot; just now</span></div>
            </div>
            <div style="padding:2px 12px 6px"><div class="pv-rd-title">${safeTitle}</div></div>
            ${media && media.length > 0
                ? `<div class="pv-letterbox" style="width:100%;max-height:340px;overflow:hidden">${renderSingleMediaItem(clampMediaForPlatform('reddit', media)[0], 'contain')}</div>`
                : ''
            }
            <div style="padding:4px 12px 8px"><div class="pv-body">${formatText(text)}</div></div>
            <div class="pv-rd-actionrow">
                <span>▲ Vote ▼</span>
                <span>💬 Comment</span>
                <span>↗ Share</span>
            </div>
        </div>
    `;
}

export function renderPreview(platform, text, title, brandName, media) {
  const actualTitle = typeof title === "string" && !title.includes("@") ? title : "";
  const actualBrandName =
    typeof title === "string" && title.includes("@")
      ? title
      : brandName || "Your Brand";

  switch (platform.toLowerCase()) {
    case "facebook":
      return renderFacebookPreview(text, actualBrandName, media);
    case "instagram":
      return renderInstagramPreview(text, actualBrandName, media);
    case "x":
    case "twitter":
      return renderXPreview(text, actualBrandName, media);
    case "linkedin":
      return renderLinkedInPreview(text, actualBrandName, media);
    case "tiktok":
      return renderTikTokPreview(text, actualBrandName, media);
    case "youtube":
    case "youtube_shorts":
      return renderYouTubePreview(text, actualTitle, actualBrandName, media);
    case "pinterest":
      return renderPinterestPreview(text, actualTitle, actualBrandName, media);
    case "reddit":
      return renderRedditPreview(text, actualTitle, actualBrandName, media);
    case "blog":
      return renderBlogPreview(text, actualTitle);
    case "website":
      return renderWebsitePreview(text, actualTitle, actualBrandName, media);
    default:
      return `<div class="pv-body" style="padding:16px;font-size:14px;color:var(--text);line-height:1.5;white-space:pre-wrap">${formatText(text)}</div>`;
  }
}
